import React, { useState } from "react";
import { IoClose } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import useAsyncAction from "../../hooks/usePropertyAction";
import { hireManager } from "../../services/hireManagerService";
import { getUserData, withAuthCheck } from "../../Utils/localStorageUtils";
import { useNavigate } from "react-router-dom";
import useCustomNavigate from "../../hooks/useCustomNavigate";
import { property_manager } from "../../Utils/export";
import { FaUserTie } from "react-icons/fa";


import { FiUserCheck } from "react-icons/fi";
import { MdManageAccounts, MdBuild } from "react-icons/md";
import { FaHandshake } from "react-icons/fa";
import { FaCertificate } from "react-icons/fa";

import { RiTeamLine } from "react-icons/ri";
import { BsClockHistory } from "react-icons/bs";
import { HiOutlineScale } from "react-icons/hi";

const fadeUpVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const fadeUpStaggerVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.2,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};


const RelationshipManager = () => {
  const navigate = useNavigate();
  const { goTo } = useCustomNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
  });

  const { execute, loading } = useAsyncAction(hireManager);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await withAuthCheck(async (userData) => {
      const response = await execute({ ...formData, userId: userData._id });
      if (response) {
        setIsOpen(false);
        setFormData({ name: "", email: "", mobile: "" });
      }
    })(goTo);
  };

  return (
    <div className="pt-[110px] items-center min-h-screen bg-[#f5f5f5]">
      {/* Banner Image */}
      <div className="w-full h-[300px] md:h-full">
        <img
          src={property_manager}
          alt="Relationship Manager Banner"
          className="object-cover w-full h-full"
        />
      </div>

      {/* Content Section */}
      <div className="max-w-[96%] mx-auto px-6 md:px-10 py-12 flex flex-col items-center">
        {/* Section 1 */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          className="mb-12"
        >
          <motion.h2
            custom={0}
            variants={fadeUpStaggerVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false }}
            className="text-4xl md:text-6xl font-satisfy text-gray-700 mb-6 text-center md:text-left"
          >
            Role of the Relationship Manager
          </motion.h2>
          <ul className="text-gray-700 text-lg md:text-xl space-y-4 leading-relaxed font-timesnewroman">
            {[
              {
                icon: <FiUserCheck className="inline mr-2 text-green-600 text-2xl" />,
                label: "Tenant Verification",
                detail: "Ensures legitimate and trustworthy tenants.",
              },
              {
                icon: <MdManageAccounts className="inline mr-2 text-purple-600 text-2xl" />,
                label: "Property Management",
                detail: "Handles maintenance and management needs.",
              },
              {
                icon: <FaHandshake className="inline mr-2 text-orange-500 text-2xl" />,
                label: "Buy/Rent Section Support",
                detail: "End-to-end assistance for buyers and renters.",
              },
            ].map((item, index) => (
              <motion.li
                key={index}
                custom={index}
                variants={fadeUpStaggerVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false }}
                className="bg-white flex items-start gap-2 text-[18px"
              >
                <span className="">{item.icon}</span>
                <span>
                  <strong>{item.label}:</strong> {item.detail}
                </span>
              </motion.li>
            ))}
          </ul>
        </motion.div>


        {/* Section 2 */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
        >
          <motion.h3
            custom={0}
            variants={fadeUpStaggerVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false }}
            className="text-3xl md:text-6xl font-satisfy text-gray-700 mb-6 text-center md:text-left"
          >
            Why Hire a Property Manager?
          </motion.h3>
          <ul className="text-gray-700 text-lg md:text-xl space-y-4 leading-relaxed font-timesnewroman">
            {[
              {
                icon: <FaCertificate className="inline mr-2 text-slate-600 text-2xl" />,
                label: "Expertise & Experience",
                detail: "Understands market and legal aspects.",
              },
              {
                icon: <RiTeamLine className="inline mr-2 text-amber-600 text-2xl" />,
                label: "Tenant Management",
                detail: "Screening, agreements, rent collection.",
              },
              {
                icon: <MdBuild className="inline mr-2 text-rose-500 text-2xl" />,
                label: "Maintenance & Repairs",
                detail: "Keeps property in excellent shape.",
              },
              {
                icon: <BsClockHistory className="inline mr-2 text-indigo-500 text-2xl" />,
                label: "Time-Saving",
                detail: "You focus on growth, they manage operations.",
              },
              {
                icon: <HiOutlineScale className="inline mr-2 text-teal-600 text-2xl" />,
                label: "Legal Compliance",
                detail: "Adheres to housing laws and regulations.",
              },
            ].map((item, index) => (
              <motion.li
                key={index}
                custom={index + 1}
                variants={fadeUpStaggerVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false }}
                className="bg-white flex items-center gap-2"
              >
                <span className="">{item.icon}</span>
                <span>
                  <strong>{item.label}:</strong> {item.detail}
                </span>
              </motion.li>
            ))}
          </ul>
        </motion.div>



        {/* CTA Button */}
        <div className="mt-10 text-center">
          <motion.button
            className="relative overflow-hidden inline-flex items-center gap-2 text-white font-medium py-3 px-6 rounded-xl shadow-md font-timesnewroman text-lg group bg-black"
            onClick={() => setIsOpen(true)}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-blue-500 via-blue-700 to-blue-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-0" />
            <span className="relative z-10 flex items-center gap-2">
              <FaUserTie className="w-5 h-5" />
              Hire a Manager
            </span>
          </motion.button>
        </div>
      </div>

      {/* Modal Form */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 flex items-center z-50 justify-center bg-black bg-opacity-50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white p-8 rounded-lg shadow-xl w-[90%] max-w-[500px] relative"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -50, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              {/* Close Button */}
              <button
                className="absolute top-4 right-4 text-gray-600 hover:text-black"
                onClick={() => setIsOpen(false)}
              >
                <IoClose size={28} />
              </button>

              <h2 className="text-3xl font-semibold text-gray-800 mb-6 text-center">
                Hire a Manager
              </h2>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-gray-700 font-medium text-lg">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-blue-300 text-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium text-lg">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-blue-300 text-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium text-lg">Mobile Number</label>
                  <input
                    type="number"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter mobile number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-blue-300 text-lg"
                    required
                  />
                </div>
                <div className="flex justify-center mt-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`${loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                      } text-white font-bold py-3 px-8 rounded-lg transition duration-300 w-full text-lg`}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RelationshipManager;
